import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  useColorScheme,
  View,
} from "react-native";
import { produtosRepository } from "../repositories/produtosRepository";
import { vendasRepository } from "../repositories/vendasRepository";
import { GRADUACOES, Produto } from "../types/database";
import {
  formatarDataBr,
  getProximoVencimento,
  getProximoVencimentoPadrao,
} from "../utils/dataVencimento";

type QuantidadesPorProduto = Record<string, number>;

function sanitizarTelefone(telefone: string): string {
  const somenteDigitos = telefone.replace(/\D/g, "");
  if (somenteDigitos.length === 11) return `55${somenteDigitos}`;
  return somenteDigitos;
}

export default function NovaVendaScreen() {
  const queryClient = useQueryClient();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const styles = createStyles(isDark);

  const [clienteNome, setClienteNome] = useState("");
  const [graduacao, setGraduacao] = useState<string>(GRADUACOES[0]);
  const [telefone, setTelefone] = useState("");
  const [observacao, setObservacao] = useState("");
  const [diaVencimento, setDiaVencimento] = useState<number>(1);
  const [pagamentoNaHora, setPagamentoNaHora] = useState(false);
  const [quantidades, setQuantidades] = useState<QuantidadesPorProduto>({});
  const [modalGraduacaoAberto, setModalGraduacaoAberto] = useState(false);

  const vencimento = useMemo(
    () =>
      pagamentoNaHora
        ? getProximoVencimentoPadrao()
        : getProximoVencimento(diaVencimento),
    [pagamentoNaHora, diaVencimento],
  );

  const {
    data: produtos,
    isLoading: carregandoProdutos,
    error: erroProdutos,
  } = useQuery({
    queryKey: ["produtos"],
    queryFn: produtosRepository.buscarTodos,
  });

  function aplicarAcrescimoFiado(nomeProduto: string): number {
    const nomeNormalizado = nomeProduto
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    return nomeNormalizado.includes("acai") && !pagamentoNaHora ? 2 : 0;
  }

  function getPrecoProdutoComAcrescimo(produto: Produto): number {
    return produto.preco + aplicarAcrescimoFiado(produto.nome);
  }

  const valorTotal = useMemo(() => {
    if (!produtos) return 0;

    return produtos.reduce((total, produto) => {
      const quantidade = quantidades[produto.id] ?? 0;
      const precoComAcrescimo = getPrecoProdutoComAcrescimo(produto);
      return total + quantidade * precoComAcrescimo;
    }, 0);
  }, [produtos, quantidades, pagamentoNaHora]);

  const criarVendaMutation = useMutation({
    mutationFn: vendasRepository.criarOuAcumular,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendas"] });
      queryClient.invalidateQueries({ queryKey: ["vendas-resumo-mes"] });
      Alert.alert("Sucesso", "Comanda salva com sucesso!", [
        { text: "OK", onPress: () => router.back() },
      ]);
    },
    onError: (error: Error) => {
      Alert.alert(
        "Erro",
        error.message || "Não foi possível salvar a comanda.",
      );
    },
  });

  function alterarQuantidade(produtoId: string, delta: number) {
    setQuantidades((atual) => {
      const quantidadeAtual = atual[produtoId] ?? 0;
      const novaQuantidade = Math.max(0, quantidadeAtual + delta);

      if (novaQuantidade === 0) {
        const { [produtoId]: _, ...resto } = atual;
        return resto;
      }

      return { ...atual, [produtoId]: novaQuantidade };
    });
  }

  function validarFormulario(): string | null {
    if (!clienteNome.trim()) return "Informe o nome do militar/cliente.";
    if (!sanitizarTelefone(telefone)) return "Informe o telefone com DDD.";
    if (valorTotal <= 0) return "Selecione ao menos um produto.";
    return null;
  }

  function handleSalvar() {
    const erroValidacao = validarFormulario();
    if (erroValidacao) {
      Alert.alert("Atenção", erroValidacao);
      return;
    }

    if (!produtos) return;

    const telefonePadronizado = sanitizarTelefone(telefone);

    const itens = produtos
      .filter((produto) => (quantidades[produto.id] ?? 0) > 0)
      .map((produto) => ({
        produto_id: produto.id,
        nome_produto: produto.nome,
        quantidade: quantidades[produto.id],
        preco_unitario: getPrecoProdutoComAcrescimo(produto),
      }));

    criarVendaMutation.mutate({
      cliente_nome: clienteNome,
      graduacao,
      telefone: telefonePadronizado,
      observacao: observacao.trim() || undefined,
      vencimento,
      modalidade: pagamentoNaHora ? "na hora" : "a prazo",
      status: "Pendente",
      itens,
    });
  }

  function renderProduto({ item }: { item: Produto }) {
    const quantidade = quantidades[item.id] ?? 0;
    const precoComAcrescimo = getPrecoProdutoComAcrescimo(item);

    return (
      <View style={styles.produtoCard}>
        <View style={styles.produtoInfo}>
          <Text style={styles.produtoNome}>{item.nome}</Text>
          <Text style={styles.produtoPreco}>
            R$ {precoComAcrescimo.toFixed(2)}
          </Text>
        </View>

        <View style={styles.quantidadeControles}>
          <Pressable
            style={[
              styles.botaoQuantidade,
              quantidade === 0 && styles.botaoQuantidadeDesabilitado,
            ]}
            onPress={() => alterarQuantidade(item.id, -1)}
            disabled={quantidade === 0}
          >
            <Text style={styles.botaoQuantidadeTexto}>−</Text>
          </Pressable>

          <Text style={styles.quantidadeValor}>{quantidade}</Text>

          <Pressable
            style={styles.botaoQuantidade}
            onPress={() => alterarQuantidade(item.id, 1)}
          >
            <Text style={styles.botaoQuantidadeTexto}>+</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (carregandoProdutos) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563eb" />
        <Text style={styles.textoCarregando}>Carregando produtos...</Text>
      </View>
    );
  }

  if (erroProdutos) {
    return (
      <View style={styles.center}>
        <Text style={styles.textoErro}>Erro ao carregar produtos.</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.conteudo}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.toggleRow}>
          <Text style={styles.toggleTexto}>Pagamento na Hora</Text>
          <Switch
            value={pagamentoNaHora}
            onValueChange={setPagamentoNaHora}
            trackColor={{ false: "#d1d5db", true: "#93c5fd" }}
            thumbColor={pagamentoNaHora ? "#2563eb" : "#f4f4f5"}
          />
        </View>

        {!pagamentoNaHora && (
          <View style={styles.secaoVencimento}>
            <Text style={styles.tituloSecaoVencimento}>Dia do Vencimento</Text>
            <View style={styles.opcoesVencimentoRow}>
              {[1, 2, 3].map((dia) => {
                const selecionado = diaVencimento === dia;

                return (
                  <TouchableOpacity
                    key={dia}
                    activeOpacity={0.8}
                    onPress={() => setDiaVencimento(dia)}
                    style={[
                      styles.botaoDiaVencimento,
                      selecionado && styles.botaoDiaVencimentoSelecionado,
                    ]}
                  >
                    <Text
                      style={[
                        styles.textoBotaoDiaVencimento,
                        selecionado &&
                          styles.textoBotaoDiaVencimentoSelecionado,
                      ]}
                    >
                      Dia {dia}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        <Text style={styles.tituloSecao}>Dados do Cliente</Text>

        <Text style={styles.label}>Nome do Militar/Cliente</Text>
        <TextInput
          style={styles.input}
          value={clienteNome}
          onChangeText={setClienteNome}
          placeholder="Ex: João Silva"
          placeholderTextColor={isDark ? "#888" : "#6b7280"}
          autoCapitalize="words"
        />

        <Text style={styles.label}>Graduação</Text>
        <Pressable
          style={styles.seletor}
          onPress={() => setModalGraduacaoAberto(true)}
        >
          <Text style={styles.seletorTexto}>{graduacao}</Text>
          <Text style={styles.seletorSeta}>▼</Text>
        </Pressable>

        <Text style={styles.label}>Telefone (com DDD)</Text>
        <TextInput
          style={styles.input}
          value={telefone}
          onChangeText={setTelefone}
          placeholder="Ex: (11) 98765-4321"
          placeholderTextColor={isDark ? "#888" : "#6b7280"}
          keyboardType="phone-pad"
        />

        <Text style={styles.label}>Observação</Text>
        <TextInput
          style={[styles.input, styles.inputMultilinha]}
          value={observacao}
          onChangeText={setObservacao}
          placeholder="Informações adicionais (opcional)"
          placeholderTextColor={isDark ? "#888" : "#6b7280"}
          multiline
          numberOfLines={3}
          textAlignVertical="top"
        />

        <Text style={styles.label}>Vencimento</Text>
        <View style={styles.vencimentoBox}>
          <Text style={styles.vencimentoTexto}>
            {formatarDataBr(vencimento)}
          </Text>
          <Text style={styles.vencimentoHint}>
            Próximo mês, dia {pagamentoNaHora ? 1 : diaVencimento}º
          </Text>
        </View>

        <Text style={styles.tituloSecao}>Produtos</Text>
        <FlatList
          data={produtos}
          keyExtractor={(item) => item.id}
          renderItem={renderProduto}
          scrollEnabled={false}
          ListEmptyComponent={
            <Text style={styles.listaVazia}>Nenhum produto cadastrado.</Text>
          }
        />

        <View style={styles.totalBox}>
          <Text style={styles.totalLabel}>Valor Total</Text>
          <Text style={styles.totalValor}>R$ {valorTotal.toFixed(2)}</Text>
        </View>

        <Pressable
          style={[
            styles.botaoSalvar,
            criarVendaMutation.isPending && styles.botaoSalvarDesabilitado,
          ]}
          onPress={handleSalvar}
          disabled={criarVendaMutation.isPending}
        >
          {criarVendaMutation.isPending ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.botaoSalvarTexto}>Salvar Comanda</Text>
          )}
        </Pressable>
      </ScrollView>

      <Modal
        visible={modalGraduacaoAberto}
        transparent
        animationType="fade"
        onRequestClose={() => setModalGraduacaoAberto(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setModalGraduacaoAberto(false)}
        >
          <View style={styles.modalConteudo}>
            <Text style={styles.modalTitulo}>Selecione a graduação</Text>
            <ScrollView>
              {GRADUACOES.map((opcao) => (
                <Pressable
                  key={opcao}
                  style={[
                    styles.modalOpcao,
                    graduacao === opcao && styles.modalOpcaoSelecionada,
                  ]}
                  onPress={() => {
                    setGraduacao(opcao);
                    setModalGraduacaoAberto(false);
                  }}
                >
                  <Text
                    style={[
                      styles.modalOpcaoTexto,
                      graduacao === opcao && styles.modalOpcaoTextoSelecionado,
                    ]}
                  >
                    {opcao}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </KeyboardAvoidingView>
  );
}

function createStyles(isDark: boolean) {
  return StyleSheet.create({
    flex: { flex: 1 },
    container: { flex: 1, backgroundColor: isDark ? "#121212" : "#f5f5f5" },
    conteudo: { padding: 20, paddingBottom: 40 },
    center: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: isDark ? "#121212" : "#f5f5f5",
    },
    textoCarregando: { marginTop: 12, color: isDark ? "#AAAAAA" : "#666" },
    textoErro: { color: "#dc2626" },
    toggleRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#d1d5db",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      marginBottom: 16,
    },
    toggleTexto: {
      fontSize: 15,
      fontWeight: "600",
      color: isDark ? "#FFFFFF" : "#111827",
    },
    secaoVencimento: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#d1d5db",
      borderRadius: 8,
      padding: 12,
      marginBottom: 16,
    },
    tituloSecaoVencimento: {
      fontSize: 15,
      fontWeight: "700",
      color: isDark ? "#FFFFFF" : "#111827",
      marginBottom: 10,
    },
    opcoesVencimentoRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 8,
    },
    botaoDiaVencimento: {
      flex: 1,
      backgroundColor: isDark ? "#2A2A2A" : "#e5e7eb",
      borderRadius: 8,
      paddingVertical: 10,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: isDark ? "#3A3A3A" : "#d1d5db",
    },
    botaoDiaVencimentoSelecionado: {
      backgroundColor: "#2563eb",
      borderColor: "#1d4ed8",
    },
    textoBotaoDiaVencimento: {
      color: isDark ? "#F3F4F6" : "#374151",
      fontWeight: "600",
    },
    textoBotaoDiaVencimentoSelecionado: {
      color: "#fff",
    },
    tituloSecao: {
      fontSize: 20,
      fontWeight: "bold",
      marginBottom: 16,
      marginTop: 8,
      color: isDark ? "#FFFFFF" : "#111",
    },
    label: {
      fontSize: 14,
      fontWeight: "600",
      marginBottom: 6,
      color: isDark ? "#E5E7EB" : "#374151",
    },
    input: {
      backgroundColor: isDark ? "#2C2C2C" : "#fff",
      borderWidth: 1,
      borderColor: isDark ? "#4B5563" : "#d1d5db",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 16,
      marginBottom: 16,
      color: isDark ? "#FFFFFF" : "#111827",
    },
    inputMultilinha: { minHeight: 80 },
    seletor: {
      backgroundColor: isDark ? "#1F2937" : "#fff",
      borderWidth: 1,
      borderColor: isDark ? "#374151" : "#d1d5db",
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      marginBottom: 16,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    seletorTexto: { fontSize: 16, color: isDark ? "#FFFFFF" : "#111" },
    seletorSeta: { fontSize: 12, color: isDark ? "#AAAAAA" : "#6b7280" },
    vencimentoBox: {
      backgroundColor: isDark ? "#172554" : "#eff6ff",
      borderRadius: 8,
      padding: 12,
      marginBottom: 16,
      borderWidth: 1,
      borderColor: isDark ? "#2563eb" : "#bfdbfe",
    },
    vencimentoTexto: {
      fontSize: 18,
      fontWeight: "bold",
      color: isDark ? "#BFDBFE" : "#1d4ed8",
    },
    vencimentoHint: {
      fontSize: 12,
      color: "#AAA",
      marginTop: 4,
    },
    produtoCard: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 8,
      padding: 12,
      marginBottom: 10,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderWidth: 1,
      borderColor: isDark ? "#2A2A2A" : "#e5e7eb",
    },
    produtoInfo: { flex: 1, marginRight: 12 },
    produtoNome: {
      fontSize: 16,
      fontWeight: "500",
      color: isDark ? "#FFFFFF" : "#111",
    },
    produtoPreco: {
      fontSize: 14,
      color: isDark ? "#4ADE80" : "#059669",
      marginTop: 4,
    },
    quantidadeControles: { flexDirection: "row", alignItems: "center", gap: 8 },
    botaoQuantidade: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: "#2563eb",
      justifyContent: "center",
      alignItems: "center",
    },
    botaoQuantidadeDesabilitado: { backgroundColor: "#d1d5db" },
    botaoQuantidadeTexto: {
      color: "#fff",
      fontSize: 20,
      fontWeight: "bold",
      lineHeight: 22,
    },
    quantidadeValor: {
      fontSize: 18,
      fontWeight: "bold",
      minWidth: 24,
      textAlign: "center",
      color: isDark ? "#FFFFFF" : "#111827",
    },
    listaVazia: {
      textAlign: "center",
      color: isDark ? "#AAAAAA" : "#6b7280",
      marginVertical: 16,
    },
    totalBox: {
      backgroundColor: isDark ? "#111827" : "#111827",
      borderRadius: 8,
      padding: 16,
      marginTop: 8,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    totalLabel: { fontSize: 16, color: "#d1d5db" },
    totalValor: { fontSize: 22, fontWeight: "bold", color: "#fff" },
    botaoSalvar: {
      backgroundColor: "#2563eb",
      borderRadius: 8,
      paddingVertical: 16,
      alignItems: "center",
      marginTop: 20,
    },
    botaoSalvarDesabilitado: { opacity: 0.7 },
    botaoSalvarTexto: { color: "#fff", fontSize: 18, fontWeight: "bold" },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.5)",
      justifyContent: "center",
      padding: 24,
    },
    modalConteudo: {
      backgroundColor: isDark ? "#1E1E1E" : "#fff",
      borderRadius: 12,
      padding: 16,
      maxHeight: "70%",
    },
    modalTitulo: {
      fontSize: 18,
      fontWeight: "bold",
      marginBottom: 12,
      color: isDark ? "#FFFFFF" : "#111",
    },
    modalOpcao: {
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderBottomWidth: 1,
      borderBottomColor: isDark ? "#2A2A2A" : "#f3f4f6",
    },
    modalOpcaoSelecionada: { backgroundColor: isDark ? "#172554" : "#eff6ff" },
    modalOpcaoTexto: { fontSize: 16, color: isDark ? "#E5E7EB" : "#374151" },
    modalOpcaoTextoSelecionado: { color: "#2563eb", fontWeight: "600" },
  });
}
